import {
  Ban,
  Bot,
  Ear,
  EarOff,
  Mountain,
  Rabbit,
  Sliders,
  Sparkle,
  User,
  UserRound,
} from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/Button";
import { Slider } from "@/components/ui/Slider";
import { tone as toneClasses, type Tone } from "@/components/ui/tone";
import { cn } from "@/lib/cn";
import { useMicPreferences } from "@/lib/audio/useMicPreferences";
import { voiceSupported } from "@/lib/audio/VoiceChanger";
import {
  MAX_FORMANT,
  MAX_SEMITONES,
  MIN_FORMANT,
  MIN_SEMITONES,
  VOICE_ORDER,
  type VoicePreset,
} from "@/lib/audio/voices";
import { useT, type MessageKey } from "@/lib/i18n";

const ICONS: Record<VoicePreset, typeof Ban> = {
  none: Ban,
  woman: UserRound,
  man: User,
  chipmunk: Rabbit,
  giant: Mountain,
  robot: Bot,
  alien: Sparkle,
  custom: Sliders,
};

const LABELS: Record<VoicePreset, MessageKey> = {
  none: "voice.none",
  woman: "voice.woman",
  man: "voice.man",
  chipmunk: "voice.chipmunk",
  giant: "voice.giant",
  robot: "voice.robot",
  alien: "voice.alien",
  custom: "voice.custom",
};

export function VoicePicker({
  tone: which = "stage",
  onApply,
  monitor,
}: {
  tone?: Tone | undefined;
  onApply?: (() => void) | undefined;

  monitor?: ((on: boolean) => boolean) | undefined;
}) {
  const { preferences, set } = useMicPreferences();
  const styles = toneClasses(which);
  const t = useT();
  const [listening, setListening] = useState(false);

  const current = preferences.voice;

  useEffect(
    () => () => {
      monitor?.(false);
    },
    [monitor],
  );

  if (!voiceSupported()) {
    return <p className={cn("text-xs", styles.muted)}>{t("voice.unsupported")}</p>;
  }

  const choose = (voice: VoicePreset) => {
    set("voice", voice);
    onApply?.();

    if (voice === "none" && listening) setListening(monitor?.(false) ?? false);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-3">
        <p className={cn("text-sm font-medium", styles.text)}>{t("voice.title")}</p>
        {monitor === undefined ? null : (
          <Button
            onClick={() => {
              const next = !listening;
              setListening(monitor(next));
            }}
            size="sm"
            tone={which}
            variant="secondary"
          >
            {listening ? (
              <EarOff aria-hidden className="h-3.5 w-3.5" />
            ) : (
              <Ear aria-hidden className="h-3.5 w-3.5" />
            )}
            {listening ? t("voice.stopListening") : t("voice.listen")}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {VOICE_ORDER.map((id) => {
          const Icon = ICONS[id];
          const active = current === id;
          return (
            <button
              aria-pressed={active}
              className={cn(
                "flex min-h-11 items-center justify-center gap-2 rounded-xl border px-2 text-sm transition-colors",
                active
                  ? cn("border-brand font-medium", styles.selected)
                  : cn(styles.control, styles.hover),
              )}
              key={id}
              onClick={() => {
                choose(id);
              }}
              type="button"
            >
              <Icon aria-hidden className="h-4 w-4 shrink-0" />
              <span className="truncate">{t(LABELS[id])}</span>
            </button>
          );
        })}
      </div>

      {current === "custom" ? (
        <div className="flex flex-col gap-3 pt-1">
          <Slider
            label={t("voice.pitch")}
            max={MAX_SEMITONES}
            maxLabel={t("voice.pitchMax")}
            min={MIN_SEMITONES}
            minLabel={t("voice.pitchMin")}
            onChange={(value) => {
              set("voiceSemitones", value);
              onApply?.();
            }}
            readout={
              preferences.voiceSemitones === 0
                ? t("voice.noShift")
                : t("voice.semitones", { value: preferences.voiceSemitones })
            }
            step={1}
            tone={which}
            value={preferences.voiceSemitones}
          />
          <Slider
            label={t("voice.formant")}
            max={Math.round(MAX_FORMANT * 100)}
            maxLabel={t("voice.formantMax")}
            min={Math.round(MIN_FORMANT * 100)}
            minLabel={t("voice.formantMin")}
            onChange={(value) => {
              set("voiceFormant", value / 100);
              onApply?.();
            }}
            readout={t("mic.percent", { value: Math.round(preferences.voiceFormant * 100) })}
            step={5}
            tone={which}
            value={Math.round(preferences.voiceFormant * 100)}
          />
          <p className={cn("text-xs", styles.muted)}>{t("voice.customBody")}</p>
        </div>
      ) : null}

      <p className={cn("text-xs", styles.muted)}>{t("voice.hint")}</p>
      {listening ? <p className={cn("text-xs", styles.muted)}>{t("voice.listenBody")}</p> : null}
    </div>
  );
}
